'use client';

import { useState } from 'react';

import { MinusIcon, PlusIcon } from 'lucide-react';

interface FaqSectionProps {
  cityName: string;
  hourlyRate: number;
}

interface FaqItem {
  question: string;
  answer: string;
}

function getFaqItems(cityName: string, hourlyRate: number): FaqItem[] {
  return [
    {
      question: `How much can I earn driving in ${cityName}?`,
      answer: `Drivers in ${cityName} report earning between R120 and R${String(hourlyRate)} per hour before expenses. Peak hours like morning commutes and Friday/Saturday nights pay more due to surge pricing.`,
    },
    {
      question: 'What is a PrDP and how do I get one?',
      answer:
        'A Professional Driving Permit is required by South African law for passenger transport. Apply at your nearest Driving Licence Testing Centre with your SA ID, valid license, and a medical certificate. Processing takes 4 to 6 weeks.',
    },
    {
      question: 'What vehicle do I need?',
      answer:
        'A 2015 model year or newer, four-door sedan or SUV in good condition with working air conditioning. Popular choices: Toyota Corolla, Volkswagen Polo, Hyundai i20.',
    },
    {
      question: 'How long does signup take?',
      answer:
        'The online application takes under 5 minutes. Document review typically takes 3 to 5 business days. The longest part is usually obtaining your PrDP if you do not already have one.',
    },
    {
      question: 'Can I drive part-time?',
      answer:
        'Yes. There are no minimum hours. Drive during evenings, weekends, or whenever it suits your schedule. Many drivers use rideshare as a side income alongside their primary job.',
    },
    {
      question: 'How does payment work?',
      answer:
        'Drivers are paid weekly via direct deposit to your South African bank account. Track earnings in real time through the app. A service fee is deducted from each fare. No upfront fees to join.',
    },
  ];
}

function FaqItemRow({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-white/10">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-lg font-medium text-white">{item.question}</span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center text-white/50">
          {open ? (
            <MinusIcon className="h-5 w-5" />
          ) : (
            <PlusIcon className="h-5 w-5" />
          )}
        </span>
      </button>
      {open && (
        <div className="pb-5">
          <p className="text-base leading-relaxed text-white/60">
            {item.answer}
          </p>
        </div>
      )}
    </div>
  );
}

export function FaqSection({ cityName, hourlyRate }: FaqSectionProps) {
  const faqs = getFaqItems(cityName, hourlyRate);

  return (
    <section
      className="w-full px-4 py-16 sm:py-20"
      style={{ backgroundColor: 'var(--landing-secondary)' }}
    >
      <div className="mx-auto max-w-6xl">
        {/* Section title */}
        <p className="mb-12 text-center text-sm tracking-[0.2em] text-white/40 uppercase">
          Frequently Asked Questions
        </p>

        <div className="mx-auto max-w-3xl">
          {faqs.map((faq, index) => (
            <FaqItemRow key={index} item={faq} />
          ))}
        </div>
      </div>
    </section>
  );
}
